import { BrowserRouter, Navigate, Route, Routes, useNavigate } from "react-router-dom";

import { ProjectLayout } from "@/components/ProjectLayout";
import { Shell } from "@/components/Shell";
import { Loading } from "@/components/ui";
import { SessionProvider, useSession } from "@/hooks/useSession";
import { AcceptInvitation } from "@/pages/AcceptInvitation";
import { Activity } from "@/pages/Activity";
import { Apps } from "@/pages/Apps";
import { ChangePassword } from "@/pages/ChangePassword";
import { PlatformAudit } from "@/pages/PlatformAudit";
import { Projects } from "@/pages/Projects";
import { Repositories } from "@/pages/Repositories";
import { RunDetail } from "@/pages/RunDetail";
import { Runs } from "@/pages/Runs";
import { Schedules } from "@/pages/Schedules";
import { ScriptDetail } from "@/pages/ScriptDetail";
import { Scripts } from "@/pages/Scripts";
import { Settings } from "@/pages/Settings";
import { SignIn } from "@/pages/SignIn";
import { TeamsConnection } from "@/pages/TeamsConnection";
import { Users } from "@/pages/Users";

/**
 * Everything behind a session.
 *
 * The API re-checks permissions on every request; this only decides what to render, so a
 * stale session shows the sign-in screen rather than a broken shell.
 */
function Authenticated() {
  const { me, loading, reload } = useSession();

  if (loading) {
    return (
      <div style={{ padding: 40, maxWidth: 640, margin: "0 auto" }}>
        <Loading rows={4} label="Loading" />
      </div>
    );
  }

  if (!me) return <SignIn onSignedIn={reload} />;

  // A password somebody else chose must not survive its first use, so this comes before
  // anything else the application would show.
  if (me.must_change_password) {
    return <ChangePassword forced onChanged={reload} />;
  }

  return <Shell />;
}

function InvitationScreen() {
  const navigate = useNavigate();
  const { reload } = useSession();

  // Accepting signs them in as somebody new. Without reloading the session the shell
  // would keep showing whoever was signed in when this page loaded — the inviting
  // admin, if they clicked the link themselves to check it.
  return (
    <AcceptInvitation
      onAccepted={() => {
        reload();
        navigate("/projects", { replace: true });
      }}
    />
  );
}

export function App() {
  return (
    <BrowserRouter>
      <SessionProvider>
        <Routes>
          <Route path="/accept-invitation" element={<InvitationScreen />} />

          <Route element={<Authenticated />}>
            <Route index element={<Navigate to="/projects" replace />} />
            <Route path="projects" element={<Projects />} />
            <Route path="notifications" element={<Activity />} />
            <Route path="account/apps" element={<Apps />} />
            <Route path="account/apps/teams" element={<TeamsConnection />} />
            <Route path="admin/users" element={<Users />} />
            <Route path="admin/audit" element={<PlatformAudit />} />

            <Route path="projects/:projectRef" element={<ProjectLayout />}>
              <Route index element={<Scripts />} />
              <Route path="scripts/:scriptRef" element={<ScriptDetail />} />
              <Route path="runs" element={<Runs />} />
              <Route path="runs/:runId" element={<RunDetail />} />
              <Route path="schedules" element={<Schedules />} />
              <Route path="repositories" element={<Repositories />} />
              <Route path="settings" element={<Settings />} />
            </Route>

            <Route path="*" element={<Navigate to="/projects" replace />} />
          </Route>
        </Routes>
      </SessionProvider>
    </BrowserRouter>
  );
}
